/**
 * Service Principal Form Component
 *
 * Feature: 023-service-principal-registry
 * Task: T023 - Create ServicePrincipalForm component with validation
 * Constitution: v1.8.0 (Multi-Subscription SQL MI Edition)
 *
 * Form for creating and editing service principals with validation
 */

import React, { useState, useEffect } from 'react';
import {
  ServicePrincipal,
  CreateServicePrincipalRequest,
  UpdateServicePrincipalRequest,
  ServicePrincipalCategory,
  VALIDATION_RULES,
} from '../types';

interface ServicePrincipalFormProps {
  initialData?: ServicePrincipal;
  onSubmit: (data: CreateServicePrincipalRequest | UpdateServicePrincipalRequest) => Promise<void>;
  onCancel: () => void;
  isSubmitting?: boolean;
  mode?: 'create' | 'edit';
}

interface FormErrors {
  applicationId?: string;
  objectId?: string;
  displayName?: string;
  purpose?: string;
  keyVaultSecretUri?: string;
  category?: string;
}

const CATEGORIES: ServicePrincipalCategory[] = [
  'Backup',
  'DataCollection',
  'Monitoring',
  'Integration',
];

/**
 * Validate GUID format
 */
function isValidGuid(value: string): boolean {
  return VALIDATION_RULES.GUID_PATTERN.test(value);
}

/**
 * Validate Key Vault URI format
 */
function isValidKeyVaultUri(value: string | null): boolean {
  if (!value) return true; // Optional field
  return VALIDATION_RULES.KEY_VAULT_URI_PATTERN.test(value);
}

/**
 * Service Principal Form Component
 */
export const ServicePrincipalForm: React.FC<ServicePrincipalFormProps> = ({
  initialData,
  onSubmit,
  onCancel,
  isSubmitting = false,
  mode = 'create',
}) => {
  const [formData, setFormData] = useState<{
    applicationId: string;
    objectId: string;
    displayName: string;
    purpose: string;
    category: ServicePrincipalCategory;
    keyVaultSecretUri: string;
    autoFixEnabled: boolean;
  }>({
    applicationId: initialData?.applicationId || '',
    objectId: initialData?.objectId || '',
    displayName: initialData?.displayName || '',
    purpose: initialData?.purpose || '',
    category: initialData?.category || 'Backup',
    keyVaultSecretUri: initialData?.keyVaultSecretUri || '',
    autoFixEnabled: initialData?.autoFixEnabled ?? true,
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});

  /**
   * Validate a single field
   */
  const validateField = (name: string, value: string | boolean): string | undefined => {
    switch (name) {
      case 'applicationId':
        if (!value || typeof value !== 'string') return 'Application ID is required';
        if (!isValidGuid(value)) return 'Must be a valid GUID format';
        return undefined;

      case 'objectId':
        if (!value || typeof value !== 'string') return 'Object ID is required';
        if (!isValidGuid(value)) return 'Must be a valid GUID format';
        return undefined;

      case 'displayName':
        if (!value || typeof value !== 'string') return 'Display name is required';
        if (typeof value === 'string' && value.length < VALIDATION_RULES.DISPLAY_NAME_MIN_LENGTH)
          return `Display name must be at least ${VALIDATION_RULES.DISPLAY_NAME_MIN_LENGTH} characters`;
        if (typeof value === 'string' && value.length > VALIDATION_RULES.DISPLAY_NAME_MAX_LENGTH)
          return `Display name cannot exceed ${VALIDATION_RULES.DISPLAY_NAME_MAX_LENGTH} characters`;
        return undefined;

      case 'keyVaultSecretUri':
        if (typeof value === 'string' && value && !isValidKeyVaultUri(value))
          return 'Must be a valid Key Vault secret URI';
        return undefined;

      case 'purpose':
        if (typeof value === 'string' && value.length > VALIDATION_RULES.PURPOSE_MAX_LENGTH)
          return `Purpose cannot exceed ${VALIDATION_RULES.PURPOSE_MAX_LENGTH} characters`;
        return undefined;

      default:
        return undefined;
    }
  };

  /**
   * Validate all fields
   */
  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    Object.entries(formData).forEach(([key, value]) => {
      const error = validateField(key, value);
      if (error) {
        newErrors[key as keyof FormErrors] = error;
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /**
   * Handle input change
   */
  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value, type } = e.target;
    const newValue = type === 'checkbox' ? (e.target as HTMLInputElement).checked : value;

    setFormData((prev) => ({ ...prev, [name]: newValue }));

    // Validate on change if field was touched
    if (touched[name]) {
      const error = validateField(name, newValue);
      setErrors((prev) => ({ ...prev, [name]: error }));
    }
  };

  /**
   * Handle field blur (mark as touched)
   */
  const handleBlur = (
    e: React.FocusEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));

    const error = validateField(name, value);
    setErrors((prev) => ({ ...prev, [name]: error }));
  };

  /**
   * Handle form submission
   */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Mark all fields as touched
    setTouched({
      applicationId: true,
      objectId: true,
      displayName: true,
      purpose: true,
      keyVaultSecretUri: true,
      category: true,
    });

    if (!validateForm()) {
      return;
    }

    const submitData = {
      applicationId: formData.applicationId.trim(),
      objectId: formData.objectId.trim(),
      displayName: formData.displayName.trim(),
      purpose: formData.purpose.trim() || undefined,
      category: formData.category,
      keyVaultSecretUri: formData.keyVaultSecretUri.trim() || undefined,
      autoFixEnabled: formData.autoFixEnabled,
    };

    await onSubmit(submitData);
  };

  return (
    <form onSubmit={handleSubmit} className='space-y-6'>
      {/* Application ID */}
      <div>
        <label htmlFor='applicationId' className='block text-sm font-medium text-[var(--color-text-secondary)]'>
          Application (Client) ID <span className='text-red-500'>*</span>
        </label>
        <input
          type='text'
          id='applicationId'
          name='applicationId'
          value={formData.applicationId}
          onChange={handleChange}
          onBlur={handleBlur}
          disabled={mode === 'edit' || isSubmitting}
          placeholder='00000000-0000-0000-0000-000000000000'
          className={`
            mt-1 block w-full rounded-md border shadow-sm px-3 py-2
            bg-[var(--color-bg-card)] text-[var(--color-text-primary)]
            ${
              errors.applicationId && touched.applicationId
                ? 'border-red-300 dark:border-red-700 focus:ring-red-500 focus:border-red-500'
                : 'border-[var(--color-border)] focus:ring-blue-500 focus:border-blue-500'
            }
            ${mode === 'edit' ? 'bg-[var(--color-bg-subtle)] cursor-not-allowed' : ''}
          `}
        />
        {errors.applicationId && touched.applicationId && (
          <p className='mt-1 text-sm text-red-600 dark:text-red-400'>{errors.applicationId}</p>
        )}
        <p className='mt-1 text-xs text-[var(--color-text-secondary)]'>
          The Application (Client) ID from Microsoft Entra ID
        </p>
      </div>

      {/* Object ID */}
      <div>
        <label htmlFor='objectId' className='block text-sm font-medium text-[var(--color-text-secondary)]'>
          Object ID <span className='text-red-500'>*</span>
        </label>
        <input
          type='text'
          id='objectId'
          name='objectId'
          value={formData.objectId}
          onChange={handleChange}
          onBlur={handleBlur}
          disabled={mode === 'edit' || isSubmitting}
          placeholder='00000000-0000-0000-0000-000000000000'
          className={`
            mt-1 block w-full rounded-md border shadow-sm px-3 py-2
            bg-[var(--color-bg-card)] text-[var(--color-text-primary)]
            ${
              errors.objectId && touched.objectId
                ? 'border-red-300 dark:border-red-700 focus:ring-red-500 focus:border-red-500'
                : 'border-[var(--color-border)] focus:ring-blue-500 focus:border-blue-500'
            }
            ${mode === 'edit' ? 'bg-[var(--color-bg-subtle)] cursor-not-allowed' : ''}
          `}
        />
        {errors.objectId && touched.objectId && (
          <p className='mt-1 text-sm text-red-600 dark:text-red-400'>{errors.objectId}</p>
        )}
        <p className='mt-1 text-xs text-[var(--color-text-secondary)]'>
          The Object ID of the service principal in Microsoft Entra ID
        </p>
      </div>

      {/* Display Name */}
      <div>
        <label htmlFor='displayName' className='block text-sm font-medium text-[var(--color-text-secondary)]'>
          Display Name <span className='text-red-500'>*</span>
        </label>
        <input
          type='text'
          id='displayName'
          name='displayName'
          value={formData.displayName}
          onChange={handleChange}
          onBlur={handleBlur}
          disabled={isSubmitting}
          placeholder='e.g., fabric-backup-agent-prod'
          className={`
            mt-1 block w-full rounded-md border shadow-sm px-3 py-2
            bg-[var(--color-bg-card)] text-[var(--color-text-primary)]
            ${
              errors.displayName && touched.displayName
                ? 'border-red-300 dark:border-red-700 focus:ring-red-500 focus:border-red-500'
                : 'border-[var(--color-border)] focus:ring-blue-500 focus:border-blue-500'
            }
          `}
        />
        {errors.displayName && touched.displayName && (
          <p className='mt-1 text-sm text-red-600 dark:text-red-400'>{errors.displayName}</p>
        )}
      </div>

      {/* Category */}
      <div>
        <label htmlFor='category' className='block text-sm font-medium text-[var(--color-text-secondary)]'>
          Category <span className='text-red-500'>*</span>
        </label>
        <select
          id='category'
          name='category'
          value={formData.category}
          onChange={handleChange}
          disabled={isSubmitting}
          className='mt-1 block w-full rounded-md border border-[var(--color-border)] bg-[var(--color-bg-card)] text-[var(--color-text-primary)] shadow-sm px-3 py-2 focus:ring-blue-500 focus:border-blue-500'
        >
          {CATEGORIES.map((cat) => (
            <option key={cat} value={cat}>
              {cat}
            </option>
          ))}
        </select>
        <p className='mt-1 text-xs text-[var(--color-text-secondary)]'>
          The category indicates the primary use case for this service principal
        </p>
      </div>

      {/* Purpose */}
      <div>
        <label htmlFor='purpose' className='block text-sm font-medium text-[var(--color-text-secondary)]'>
          Purpose / Description
        </label>
        <textarea
          id='purpose'
          name='purpose'
          value={formData.purpose}
          onChange={handleChange}
          onBlur={handleBlur}
          disabled={isSubmitting}
          rows={3}
          placeholder='Describe the purpose and responsibilities of this service principal...'
          className={`
            mt-1 block w-full rounded-md border shadow-sm px-3 py-2
            bg-[var(--color-bg-card)] text-[var(--color-text-primary)]
            ${
              errors.purpose && touched.purpose
                ? 'border-red-300 dark:border-red-700 focus:ring-red-500 focus:border-red-500'
                : 'border-[var(--color-border)] focus:ring-blue-500 focus:border-blue-500'
            }
          `}
        />
        {errors.purpose && touched.purpose && (
          <p className='mt-1 text-sm text-red-600 dark:text-red-400'>{errors.purpose}</p>
        )}
        <p className='mt-1 text-xs text-[var(--color-text-secondary)]'>
          {formData.purpose.length}/{VALIDATION_RULES.PURPOSE_MAX_LENGTH} characters
        </p>
      </div>

      {/* Key Vault Secret URI */}
      <div>
        <label htmlFor='keyVaultSecretUri' className='block text-sm font-medium text-[var(--color-text-secondary)]'>
          Key Vault Secret URI
        </label>
        <input
          type='text'
          id='keyVaultSecretUri'
          name='keyVaultSecretUri'
          value={formData.keyVaultSecretUri}
          onChange={handleChange}
          onBlur={handleBlur}
          disabled={isSubmitting}
          placeholder='https://your-keyvault.vault.azure.net/secrets/your-secret'
          className={`
            mt-1 block w-full rounded-md border shadow-sm px-3 py-2
            bg-[var(--color-bg-card)] text-[var(--color-text-primary)]
            ${
              errors.keyVaultSecretUri && touched.keyVaultSecretUri
                ? 'border-red-300 dark:border-red-700 focus:ring-red-500 focus:border-red-500'
                : 'border-[var(--color-border)] focus:ring-blue-500 focus:border-blue-500'
            }
          `}
        />
        {errors.keyVaultSecretUri && touched.keyVaultSecretUri && (
          <p className='mt-1 text-sm text-red-600 dark:text-red-400'>{errors.keyVaultSecretUri}</p>
        )}
        <p className='mt-1 text-xs text-[var(--color-text-secondary)]'>
          Optional: Link to the client secret stored in Azure Key Vault
        </p>
      </div>

      {/* Auto-Fix Toggle */}
      <div className='flex items-center'>
        <input
          type='checkbox'
          id='autoFixEnabled'
          name='autoFixEnabled'
          checked={formData.autoFixEnabled}
          onChange={handleChange}
          disabled={isSubmitting}
          className='h-4 w-4 text-[var(--color-accent)] focus:ring-blue-500 border-[var(--color-border)] rounded'
        />
        <label htmlFor='autoFixEnabled' className='ml-2 block text-sm text-[var(--color-text-secondary)]'>
          Enable automatic permission fixes
        </label>
      </div>
      <p className='text-xs text-[var(--color-text-secondary)] -mt-4 ml-6'>
        When enabled, missing permissions will be automatically restored during reconciliation
      </p>

      {/* Form Actions */}
      <div className='flex justify-end space-x-3 pt-4 border-t border-[var(--color-border)]'>
        <button
          type='button'
          onClick={onCancel}
          disabled={isSubmitting}
          className='px-4 py-2 text-sm font-medium text-[var(--color-text-secondary)] bg-[var(--color-bg-card)] border border-[var(--color-border)] rounded-md hover:bg-[var(--color-bg-subtle)] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500'
        >
          Cancel
        </button>
        <button
          type='submit'
          disabled={isSubmitting}
          className='px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed'
        >
          {isSubmitting ? (
            <>
              <span className='inline-block animate-spin mr-2'>⏳</span>
              {mode === 'create' ? 'Creating...' : 'Saving...'}
            </>
          ) : mode === 'create' ? (
            'Create Service Principal'
          ) : (
            'Save Changes'
          )}
        </button>
      </div>
    </form>
  );
};

export default ServicePrincipalForm;
